import type {
  ConnectionStatus,
  ConnectorListItem,
  ConnectorType,
  FailureCategory,
  MigrationBatch,
  NativeConnectorStatus,
  NativeIncident,
  ServerBuildItem,
} from "@investhinks/api-client";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import { connectorsEndpoints } from "../../../../api";

const CONNECTION_LABEL: Record<ConnectionStatus, { label: string; color: string; rank: number }> = {
  CONNECTED: { label: "Connected", color: "success", rank: 0 },
  DEGRADED: { label: "Degraded", color: "warning", rank: 1 },
  FALLBACK: { label: "Fallback", color: "danger", rank: 2 },
  NOT_RUNNING: { label: "Not running", color: "secondary", rank: 3 },
};

const CATEGORY_LABEL: Record<FailureCategory, { label: string; color: string }> = {
  TRADING_NOT_ALLOWED_ON_SERVER: { label: "Trading not allowed on server", color: "danger" },
  INVALID_CREDENTIALS: { label: "Invalid credentials", color: "danger" },
  BROKER_UNREACHABLE: { label: "Broker unreachable", color: "warning" },
  UNSUPPORTED_SERVER: { label: "Unsupported server", color: "danger" },
  SESSION_LOST: { label: "Session lost", color: "warning" },
  TRADE_REJECTED: { label: "Trade rejected", color: "warning" },
  WRITE_UNCERTAIN: { label: "Write uncertain", color: "danger" },
  RECONCILIATION: { label: "Reconciliation", color: "danger" },
  STOPLOSS: { label: "Stop-loss", color: "danger" },
  TICK_ERROR: { label: "Tick error", color: "warning" },
  VALUATION: { label: "Valuation", color: "warning" },
  UNKNOWN: { label: "Unknown", color: "secondary" },
};

const CategoryBadge: React.FC<{ category: string; raw?: string }> = ({ category, raw }) => {
  const known = CATEGORY_LABEL[category as FailureCategory] ?? CATEGORY_LABEL.UNKNOWN;
  return (
    <Badge color={known.color} title={raw}>
      {known.label}
    </Badge>
  );
};

const formatLeft = (ms: number): string => {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return seconds >= 120 ? `${Math.round(seconds / 60)}m` : `${seconds}s`;
};

const ConnectorBadge: React.FC<{ item: ConnectorListItem }> = ({ item }) => (
  <>
    <Badge color={item.connector === "METAAPI" ? "secondary" : "primary"}>
      {item.connector === "METAAPI" ? "MetaApi" : "MT5 native"}
    </Badge>
    {item.fallbackActive && (
      <Badge color="danger" className="ml-1">
        fallback active
      </Badge>
    )}
    {item.awaitingMetaapiStop && (
      <Badge color="info" className="ml-1">
        awaiting MetaApi stop
      </Badge>
    )}
    {item.metaapiAccountMissing && (
      <Badge color="warning" className="ml-1" title="Will be recreated on fallback">
        MetaApi fallback account missing
      </Badge>
    )}
  </>
);

const ConnectionBadge: React.FC<{ item: ConnectorListItem }> = ({ item }) => {
  const { label, color } = CONNECTION_LABEL[item.connection.status];
  const cause = item.fallbackCause;
  return (
    <>
      <Badge color={color}>{label}</Badge>
      <div className="text-muted small">{item.connection.detail}</div>
      {cause && (
        <div className="small">
          <CategoryBadge category={cause.category} raw={cause.message} /> {moment(cause.at).format("HH:mm")}{" "}
          <span className="text-muted" title={cause.message}>
            {cause.message.length > 70 ? `${cause.message.slice(0, 70)}…` : cause.message}
          </span>
        </div>
      )}
    </>
  );
};

/** A failed test while the account is running fine is only a test result, not an error. */
const LastTest: React.FC<{ item: ConnectorListItem }> = ({ item }) => {
  const test = item.lastTest;
  if (!test) return <small className="text-muted">never</small>;
  const when = moment(test.createdAt).fromNow();
  if (!test.ok && item.connection.status === "CONNECTED") {
    return <small className="text-muted">failed {when} (test only)</small>;
  }
  return (
    <>
      <Badge color={test.ok ? "success" : "danger"}>{test.ok ? "passed" : (test.errorClass ?? "failed")}</Badge>{" "}
      <small className="text-muted">
        {when}
        {test.serverBuild ? ` · build ${test.serverBuild}` : ""}
      </small>
    </>
  );
};

const BuildStatusBadge: React.FC<{ row: ServerBuildItem }> = ({ row }) => {
  if (row.status === "KNOWN") return <Badge color="success">Known</Badge>;
  return row.acknowledgedAt ? (
    <Badge color="info" title={`Acknowledged ${moment(row.acknowledgedAt).fromNow()} by ${row.acknowledgedBy ?? "n/a"}`}>
      Acknowledged
    </Badge>
  ) : (
    <Badge color="warning">NEW</Badge>
  );
};

type SortKey = "name" | "user" | "server" | "connector" | "connection" | "bots" | "lastTest" | "eligible";
type SortState = { key: SortKey; direction: 1 | -1 };

/** Sort values; nulls/never-tested always sort last regardless of direction. */
const sortValue = (item: ConnectorListItem, key: SortKey): string | number | null => {
  switch (key) {
    case "name":
      return item.name.toLowerCase();
    case "user":
      return item.userEmail?.toLowerCase() ?? null;
    case "server":
      return `${item.server} ${item.username}`.toLowerCase();
    case "connector":
      return `${item.connector}${item.fallbackActive ? "-fallback" : ""}`;
    case "connection":
      return item.connection.evidenceAt ? new Date(item.connection.evidenceAt).getTime() : null;
    case "bots":
      return item.botsEnabled * 1000 + item.botsTotal;
    case "lastTest":
      return item.lastTest ? new Date(item.lastTest.createdAt).getTime() : null;
    case "eligible":
      return item.eligible ? 1 : 0;
  }
};

const compare = (a: ConnectorListItem, b: ConnectorListItem, sort: SortState): number => {
  const left = sortValue(a, sort.key);
  const right = sortValue(b, sort.key);
  if (left === right) return a.name.localeCompare(b.name);
  if (left === null) return 1;
  if (right === null) return -1;
  return (left < right ? -1 : 1) * sort.direction;
};

const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string; color?: string }> = ({ label, value, hint, color }) => (
  <Col xs="6" md="4" xl="2" className="mb-3">
    <div className="border rounded p-2 h-100">
      <div className="text-muted small text-uppercase">{label}</div>
      <div className={`h3 mb-0 ${color ? `text-${color}` : ""}`}>{value}</div>
      {hint && <div className="text-muted small">{hint}</div>}
    </div>
  </Col>
);

const AttentionCard: React.FC<{ label: string; count: number; tone: string; hint: string; target: string }> = ({
  label,
  count,
  tone,
  hint,
  target,
}) => (
  <Col xs="6" md="4" xl className="mb-3">
    <div
      role="button"
      className={`border rounded p-2 h-100 ${count ? `border-${tone}` : ""}`}
      style={count ? { borderWidth: 2 } : undefined}
      onClick={() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth" })}
    >
      <div className="text-muted small text-uppercase">{label}</div>
      <div className={`h2 mb-0 ${count ? `text-${tone}` : "text-muted"}`}>{count}</div>
      <div className="text-muted small text-truncate">{hint}</div>
    </div>
  </Col>
);

const ITEM_COLOR: Record<string, string> = {
  QUEUED: "secondary",
  SWITCHING: "info",
  VERIFYING: "info",
  RETRY_WAIT: "warning",
  DONE: "success",
  FAILED: "danger",
  SKIPPED: "light",
};

const errorText = (e: unknown): string => (e as Error)?.message ?? String(e);

const DISMISSED_BATCHES_KEY = "connectors.dismissedMigrationBatches";

/** Admin list of MetaTrader credentials with their connector, live connection, mass migration and incident log. */
const Connectors: React.FC = () => {
  const [status, setStatus] = useState<NativeConnectorStatus | null>(null);
  const [items, setItems] = useState<ConnectorListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortState>({ key: "connection", direction: -1 });
  const [builds, setBuilds] = useState<ServerBuildItem[] | null>(null);
  const [buildsError, setBuildsError] = useState<string | null>(null);
  const [acknowledging, setAcknowledging] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  // migration
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [migrateTarget, setMigrateTarget] = useState<ConnectorType | null>(null);
  const [concurrency, setConcurrency] = useState(3);
  const [delayMinutes, setDelayMinutes] = useState(2);
  const [migrating, setMigrating] = useState(false);
  const [migrateError, setMigrateError] = useState<string | null>(null);
  const [testingServer, setTestingServer] = useState<string | null>(null);
  const [batches, setBatches] = useState<MigrationBatch[] | null>(null);
  // Batches the admin has seen and hidden (per browser).
  const [dismissedBatches, setDismissedBatches] = useState<Set<number>>(() => {
    try {
      return new Set<number>(JSON.parse(localStorage.getItem(DISMISSED_BATCHES_KEY) ?? "[]"));
    } catch {
      return new Set<number>();
    }
  });
  const [showDismissedBatches, setShowDismissedBatches] = useState(false);
  const [batchesError, setBatchesError] = useState<string | null>(null);
  const [openBatches, setOpenBatches] = useState<Set<number>>(new Set());

  // incidents
  const [incidents, setIncidents] = useState<NativeIncident[] | null>(null);
  const [incidentsError, setIncidentsError] = useState<string | null>(null);
  const [incidentAccount, setIncidentAccount] = useState("");
  const [incidentSeverity, setIncidentSeverity] = useState("");
  const [incidentCategory, setIncidentCategory] = useState("");
  const [openIncidents, setOpenIncidents] = useState<Set<number>>(new Set());
  const [showAcknowledgedIncidents, setShowAcknowledgedIncidents] = useState(false);
  const [selectedIncidents, setSelectedIncidents] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    try {
      const result = await connectorsEndpoints.listConnectors();
      setStatus(result.status);
      setItems(result.credentials);
      setError(null);
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  const loadBuilds = useCallback(async () => {
    try {
      setBuilds((await connectorsEndpoints.listServerBuilds()).builds);
      setBuildsError(null);
    } catch (e) {
      setBuildsError(errorText(e));
    }
  }, []);

  const loadBatches = useCallback(async () => {
    try {
      setBatches(await connectorsEndpoints.listMigrations());
      setBatchesError(null);
    } catch (e) {
      setBatchesError(errorText(e));
    }
  }, []);

  const loadIncidents = useCallback(async () => {
    try {
      setIncidents((await connectorsEndpoints.listIncidents()).incidents);
      setIncidentsError(null);
    } catch (e) {
      setIncidentsError(errorText(e));
    }
  }, []);

  useEffect(() => {
    void load();
    void loadBuilds();
    void loadBatches();
    void loadIncidents();
  }, [load, loadBuilds, loadBatches, loadIncidents]);

  useEffect(() => {
    const timer = setInterval(() => {
      void load();
      void loadBuilds();
    }, 60_000);
    return () => clearInterval(timer);
  }, [load, loadBuilds]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // poll while a migration is running
  const anyRunning = batches?.some((batch) => batch.status === "RUNNING" || batch.items.some((i) => ["SWITCHING", "VERIFYING", "RETRY_WAIT"].includes(i.status)));
  useEffect(() => {
    if (!anyRunning) return undefined;
    const timer = setInterval(() => {
      void loadBatches();
      void load();
    }, 10_000);
    return () => clearInterval(timer);
  }, [anyRunning, loadBatches, load]);

  const acknowledge = async (row: ServerBuildItem) => {
    setAcknowledging(row.id);
    try {
      await connectorsEndpoints.acknowledgeServerBuild({ server: row.server, build: row.build });
      await loadBuilds();
    } catch (e) {
      setBuildsError(errorText(e));
    } finally {
      setAcknowledging(null);
    }
  };

  // Default view: live accounts only. Archived and broker-rejected (INVALID) credentials are behind the toggle.
  const active = useMemo(() => items?.filter((item) => !item.archived && item.state !== "INVALID") ?? [], [items]);
  const archivedCount = (items?.length ?? 0) - active.length;
  const byUid = useMemo(() => new Map((items ?? []).map((item) => [item.uid, item])), [items]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (showArchived ? items ?? [] : active)
      .filter(
        (item) =>
          !term ||
          [item.name, item.userEmail ?? "", item.server, item.username].some((field) => field.toLowerCase().includes(term))
      )
      .sort((a, b) => compare(a, b, sort));
  }, [items, active, showArchived, search, sort]);

  // Statistics always describe non-archived accounts, independent of search.
  const stats = useMemo(() => {
    const native = active.filter((item) => item.connector === "MT5_NATIVE");
    const tested = active.filter((item) => item.lastTest);
    return {
      accounts: active.length,
      native: native.length,
      nativeBots: native.reduce((sum, item) => sum + item.botsEnabled, 0),
      metaapiBots: active.filter((item) => item.connector === "METAAPI").reduce((sum, item) => sum + item.botsEnabled, 0),
      enabledBots: active.reduce((sum, item) => sum + item.botsEnabled, 0),
      eligible: active.filter((item) => item.eligible).length,
      testedPassed: tested.filter((item) => item.lastTest?.ok).length,
      testedFailed: tested.filter((item) => !item.lastTest?.ok).length,
      connected: active.filter((item) => item.connection.status === "CONNECTED").length,
      degraded: active.filter((item) => item.connection.status === "DEGRADED").length,
      notRunning: active.filter((item) => item.connection.status === "NOT_RUNNING").length,
      fallbacks: active.filter((item) => item.fallbackActive).length,
      missingMetaapi: native.filter((item) => item.metaapiAccountMissing).length,
      servers: new Set(native.map((item) => item.server)).size,
    };
  }, [active]);

  const fallbacks = active.filter((item) => item.fallbackActive);
  const serverNames = useMemo(() => [...new Set(active.map((item) => item.server))].sort(), [active]);

  const header = (key: SortKey, label: string) => (
    <th
      role="button"
      className="text-nowrap"
      onClick={() => setSort((current) => ({ key, direction: current.key === key ? (current.direction === 1 ? -1 : 1) : 1 }))}
    >
      {label} {sort.key === key ? (sort.direction === 1 ? "▲" : "▼") : <span className="text-muted">↕</span>}
    </th>
  );

  // ---- selection and migration ----
  const toggle = (uid: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  const allVisibleSelected = visible.length > 0 && visible.every((item) => selected.has(item.uid));
  const toggleAllVisible = () =>
    setSelected((current) => {
      const next = new Set(current);
      if (allVisibleSelected) visible.forEach((item) => next.delete(item.uid));
      else visible.forEach((item) => next.add(item.uid));
      return next;
    });
  const eligibleByServer = useMemo(() => {
    const counts = new Map<string, number>();
    active.filter((item) => item.eligible && item.connector === "METAAPI").forEach((item) => counts.set(item.server, (counts.get(item.server) ?? 0) + 1));
    return counts;
  }, [active]);
  const selectEligibleOnServer = (serverPick: string) => {
    if (!serverPick) return;
    setSelected((current) => {
      const next = new Set(current);
      active.filter((item) => item.server === serverPick && item.eligible && item.connector === "METAAPI").forEach((item) => next.add(item.uid));
      return next;
    });
  };

  const selectedItems = useMemo(() => [...selected].map((uid) => byUid.get(uid)).filter((item): item is ConnectorListItem => !!item), [selected, byUid]);
  const selectedServers = useMemo(() => {
    const map = new Map<string, ConnectorListItem[]>();
    selectedItems.forEach((item) => map.set(item.server, [...(map.get(item.server) ?? []), item]));
    return map;
  }, [selectedItems]);
  const windowOpen = (server: string): boolean => {
    const first = (selectedServers.get(server) ?? [])[0];
    const window = first?.serverSwitchWindow;
    return !!first?.serverLiveNative || (!!window && new Date(window.until).getTime() > now);
  };
  const nativeNeedsTests = migrateTarget === "MT5_NATIVE" && [...selectedServers.keys()].some((server) => !windowOpen(server));

  /** Runs one server test; returns null when it passed, otherwise why it did not. */
  const testServer = async (server: string): Promise<string | null> => {
    const account = (selectedServers.get(server) ?? []).find((item) => item.exchangeAccountId && item.eligible);
    if (!account?.exchangeAccountId) return `${server}: no eligible account can run a test`;
    setTestingServer(server);
    try {
      const report = await connectorsEndpoints.testConnector(account.exchangeAccountId);
      return report.ok ? null : `${server}: ${report.errorClass ?? "failed"}${report.errorMessage ? ` (${report.errorMessage})` : ""}`;
    } catch (e) {
      return `${server}: ${errorText(e)}`;
    } finally {
      setTestingServer(null);
    }
  };

  const runServerTest = async (server: string) => {
    const failure = await testServer(server);
    setMigrateError(failure);
    await load();
  };

  // Test all: servers one by one; a failure is skipped and retried once after the others.
  const [testingAll, setTestingAll] = useState(false);
  const runAllServerTests = async () => {
    const servers = [...selectedServers.keys()].filter((server) => !windowOpen(server));
    setTestingAll(true);
    setMigrateError(null);
    try {
      const failed: string[] = [];
      for (const server of servers) if (await testServer(server)) failed.push(server);
      const still: string[] = [];
      for (const server of failed) {
        const failure = await testServer(server);
        if (failure) still.push(failure);
      }
      setMigrateError(still.length ? `Failed after a retry: ${still.join("; ")}` : null);
    } finally {
      setTestingAll(false);
      await load();
    }
  };

  const startMigration = async () => {
    if (!migrateTarget) return;
    setMigrating(true);
    try {
      const result = await connectorsEndpoints.createMigration({
        uids: [...selected],
        targetConnector: migrateTarget,
        maxConcurrent: concurrency,
        delayBetweenMs: Math.round(delayMinutes * 60_000),
      });
      setMigrateTarget(null);
      setSelected(new Set());
      setMigrateError(null);
      setBatchesError(result.skipped.length ? `${result.skipped.length} skipped: ${result.skipped.map((s) => s.reason).join("; ")}` : null);
      await loadBatches();
    } catch (e) {
      setMigrateError(errorText(e));
    } finally {
      setMigrating(false);
    }
  };

  const control = async (batch: MigrationBatch, action: "pause" | "resume" | "cancel") => {
    try {
      await connectorsEndpoints.controlMigration(batch.id, action);
      await loadBatches();
    } catch (e) {
      setBatchesError(errorText(e));
    }
  };

  // ---- incidents ----
  const incidentAccounts = useMemo(
    () => [...new Map((incidents ?? []).map((i) => [i.credentialUid, i.accountName ?? i.credentialUid])).entries()],
    [incidents]
  );
  const shownIncidents = (incidents ?? []).filter(
    (i) =>
      (!incidentAccount || i.credentialUid === incidentAccount) &&
      (!incidentSeverity || i.severity === incidentSeverity) &&
      (!incidentCategory || i.category === incidentCategory) &&
      (showAcknowledgedIncidents || !i.acknowledgedAt)
  );
  const selectableIncidents = shownIncidents.filter((i) => !i.acknowledgedAt);
  const selectedShownIncidents = selectableIncidents.filter((i) => selectedIncidents.has(i.id));
  const allIncidentsSelected = selectableIncidents.length > 0 && selectedShownIncidents.length === selectableIncidents.length;
  const toggleIncidentSelection = (id: number) =>
    setSelectedIncidents((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const unacknowledgedAttention = (incidents ?? []).filter((i) => i.severity !== "WARNING" && !i.acknowledgedAt);
  const acknowledgeIncident = async (ids: number[]) => {
    try {
      await connectorsEndpoints.acknowledgeIncidents({ ids });
      setSelectedIncidents((current) => new Set([...current].filter((id) => !ids.includes(id))));
      await loadIncidents();
    } catch (e) {
      setIncidentsError(errorText(e));
    }
  };
  const isBatchFinished = (batch: MigrationBatch) => batch.status !== "RUNNING" && batch.status !== "PAUSED";
  const shownBatches = (batches ?? []).filter((batch) => showDismissedBatches || !dismissedBatches.has(batch.id));
  const dismissBatch = (id: number) => {
    const next = new Set(dismissedBatches).add(id);
    setDismissedBatches(next);
    try {
      localStorage.setItem(DISMISSED_BATCHES_KEY, JSON.stringify([...next]));
    } catch {
      /* hiding only lasts for this session */
    }
  };
  const retryFailedItems = (batch: MigrationBatch) => {
    setSelected(new Set(batch.items.filter((item) => item.status === "FAILED").map((item) => item.credentialUid)));
    setMigrateTarget(batch.targetConnector);
  };
  const failedMigrationItems = (batches ?? [])
    .filter((batch) => !dismissedBatches.has(batch.id))
    .filter((batch) => Date.now() - new Date(batch.createdAt).getTime() < 7 * 86_400_000)
    .flatMap((batch) => batch.items.filter((item) => item.status === "FAILED"))
    .map((item) => ({ ...item, accountName: byUid.get(item.credentialUid)?.name ?? null }));
  const runningBatches = (batches ?? []).filter((batch) => batch.status === "RUNNING" || batch.status === "PAUSED").length;
  const newBuilds = (builds ?? []).filter((row) => row.status === "NEW" && !row.acknowledgedAt).length;
  const copy = (text: string) => {
    try {
      void navigator.clipboard?.writeText(text);
    } catch {
      /* clipboard not available */
    }
  };

  return (
    <Container fluid className="mt-4">
      <Row>
        <AttentionCard
          label="Failed migrations"
          count={failedMigrationItems.length}
          tone="danger"
          hint={failedMigrationItems.length ? failedMigrationItems.slice(0, 3).map((i) => i.accountName ?? i.credentialUid.slice(0, 8)).join(", ") : "none"}
          target="connectors-migrations"
        />
        <AttentionCard
          label="Running migrations"
          count={runningBatches}
          tone="info"
          hint={runningBatches ? "in progress" : "none"}
          target="connectors-migrations"
        />
        <AttentionCard
          label="Incidents to review"
          count={unacknowledgedAttention.length}
          tone="danger"
          hint={unacknowledgedAttention.length ? unacknowledgedAttention[0].message.slice(0, 60) : "none unacknowledged"}
          target="connectors-incidents"
        />
        <AttentionCard
          label="Active fallbacks"
          count={stats.fallbacks}
          tone="danger"
          hint={fallbacks.length ? fallbacks.slice(0, 3).map((f) => f.name).join(", ") : "none"}
          target="connectors-accounts"
        />
        <AttentionCard
          label="New server builds"
          count={newBuilds}
          tone="warning"
          hint={newBuilds ? "acknowledge after review" : "none"}
          target="connectors-builds"
        />
      </Row>

      <Card className="mt-4" id="connectors-migrations">
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Migrations</h3>
          <span className="d-flex align-items-center">
            <label className="d-flex align-items-center mb-0 mr-3 small text-nowrap" style={{ cursor: "pointer" }}>
              <input type="checkbox" className="mr-1" checked={showDismissedBatches} onChange={(e) => setShowDismissedBatches(e.target.checked)} />
              Show acknowledged
            </label>
            <Button size="sm" outline onClick={() => void loadBatches()}>
              Refresh
            </Button>
          </span>
        </CardHeader>
        <CardBody>
          {batchesError && <Alert color="warning">{batchesError}</Alert>}
          {!batches && !batchesError && <Spinner />}
          {batches && shownBatches.length === 0 && (
            <small className="text-muted">
              {batches.length === 0 ? "No migration batches yet. Select accounts above to start one." : "No migrations to review."}
            </small>
          )}
          {shownBatches.map((batch) => {
            const count = (...statuses: string[]) => batch.items.filter((i) => statuses.includes(i.status)).length;
            const total = batch.items.length || 1;
            const open = openBatches.has(batch.id);
            return (
              <div key={batch.id} className="border rounded p-2 mb-3">
                <div className="d-flex flex-wrap align-items-center mb-2">
                  <strong className="mr-2">
                    #{batch.id} → {batch.targetConnector === "METAAPI" ? "MetaApi" : "MT5 native"}
                  </strong>
                  <Badge color={batch.status === "RUNNING" ? "info" : batch.status === "PAUSED" ? "warning" : batch.status === "DONE" ? "success" : "secondary"}>
                    {batch.status}
                  </Badge>
                  <small className="text-muted ml-2">
                    {moment(batch.createdAt).fromNow()} · by {batch.createdBy} · max {batch.maxConcurrent} at a time, {Math.round(batch.delayBetweenMs / 60000)} min apart ·
                    failures {batch.failures}/{batch.failureLimit}
                  </small>
                  <span className="ml-auto">
                    {batch.status === "RUNNING" && (
                      <Button size="sm" outline color="warning" className="mr-1" onClick={() => void control(batch, "pause")}>
                        Pause
                      </Button>
                    )}
                    {batch.status === "PAUSED" && (
                      <Button size="sm" color="primary" className="mr-1" onClick={() => void control(batch, "resume")}>
                        Resume
                      </Button>
                    )}
                    {(batch.status === "RUNNING" || batch.status === "PAUSED") && (
                      <Button size="sm" outline color="danger" className="mr-1" onClick={() => void control(batch, "cancel")}>
                        Cancel
                      </Button>
                    )}
                    {isBatchFinished(batch) && count("FAILED") > 0 && (
                      <Button size="sm" color="warning" className="mr-1" onClick={() => retryFailedItems(batch)}>
                        Retry {count("FAILED")} failed
                      </Button>
                    )}
                    {isBatchFinished(batch) && !dismissedBatches.has(batch.id) && (
                      <Button size="sm" outline className="mr-1" onClick={() => dismissBatch(batch.id)}>
                        Acknowledge
                      </Button>
                    )}
                    <Button
                      size="sm"
                      color="link"
                      onClick={() =>
                        setOpenBatches((current) => {
                          const next = new Set(current);
                          if (next.has(batch.id)) next.delete(batch.id);
                          else next.add(batch.id);
                          return next;
                        })
                      }
                    >
                      {open ? "Hide" : "Details"}
                    </Button>
                  </span>
                </div>
                {batch.pauseReason && batch.status === "PAUSED" && <Alert color="warning" className="py-1">{batch.pauseReason}</Alert>}
                <Progress multi>
                  <Progress bar color="success" value={(count("DONE") / total) * 100}>
                    {count("DONE")}
                  </Progress>
                  <Progress bar color="info" value={(count("SWITCHING", "VERIFYING", "RETRY_WAIT") / total) * 100}>
                    {count("SWITCHING", "VERIFYING", "RETRY_WAIT")}
                  </Progress>
                  <Progress bar color="danger" value={(count("FAILED") / total) * 100}>
                    {count("FAILED")}
                  </Progress>
                  <Progress bar color="secondary" value={(count("QUEUED", "SKIPPED") / total) * 100}>
                    {count("QUEUED", "SKIPPED")}
                  </Progress>
                </Progress>
                <small className="text-muted">
                  {count("DONE")} done · {count("SWITCHING", "VERIFYING", "RETRY_WAIT")} in progress · {count("QUEUED")} queued · {count("FAILED")} failed · {count("SKIPPED")} skipped
                </small>
                {open && (
                  <Table size="sm" responsive className="mt-2">
                    <thead>
                      <tr>
                        <th>Account</th>
                        <th>Status</th>
                        <th>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batch.items.map((item) => {
                        const retryIn = item.nextAttemptAt ? new Date(item.nextAttemptAt).getTime() - now : 0;
                        return (
                          <tr key={item.id}>
                            <td>{byUid.get(item.credentialUid)?.name ?? item.credentialUid}</td>
                            <td>
                              <Badge color={ITEM_COLOR[item.status]}>{item.status}</Badge>
                            </td>
                            <td>
                              {item.status === "RETRY_WAIT" && (
                                <span>
                                  retry {item.attempts}/2 {retryIn > 0 ? `in ${formatLeft(retryIn)}` : "now"}{" "}
                                </span>
                              )}
                              {item.status !== "RETRY_WAIT" && item.attempts > 1 && <span className="text-muted">attempt {item.attempts}/3 </span>}
                              <small className={item.status === "FAILED" ? "text-danger" : "text-muted"}>{item.error}</small>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </div>
            );
          })}
        </CardBody>
      </Card>

      <Card className="mt-4" id="connectors-incidents">
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Incidents</h3>
          <span>
            {selectedShownIncidents.length > 0 && (
              <Button size="sm" color="primary" className="mr-2" onClick={() => void acknowledgeIncident(selectedShownIncidents.map((i) => i.id))}>
                Acknowledge {selectedShownIncidents.length} selected
              </Button>
            )}
            {unacknowledgedAttention.length > 0 && (
              <Button size="sm" color="warning" className="mr-2" onClick={() => void acknowledgeIncident(unacknowledgedAttention.map((i) => i.id))}>
                Acknowledge {unacknowledgedAttention.length} error/fallback incident(s)
              </Button>
            )}
            <Button size="sm" outline onClick={() => void loadIncidents()}>
              Refresh
            </Button>
          </span>
        </CardHeader>
        <CardBody>
          {incidentsError && <Alert color="danger">{incidentsError}</Alert>}
          {!incidents && !incidentsError && <Spinner />}
          {incidents && (
            <div className="d-flex flex-wrap mb-3">
              <Input bsSize="sm" type="select" className="mr-2 mb-2" style={{ maxWidth: 220 }} value={incidentAccount} onChange={(e) => setIncidentAccount(e.target.value)}>
                <option value="">All accounts</option>
                {incidentAccounts.map(([uid, name]) => (
                  <option key={uid} value={uid}>
                    {name}
                  </option>
                ))}
              </Input>
              <Input bsSize="sm" type="select" className="mr-2 mb-2" style={{ maxWidth: 160 }} value={incidentSeverity} onChange={(e) => setIncidentSeverity(e.target.value)}>
                <option value="">All severities</option>
                <option value="WARNING">Warning</option>
                <option value="ERROR">Error</option>
                <option value="FALLBACK">Fallback</option>
              </Input>
              <Input bsSize="sm" type="select" className="mr-2 mb-2" style={{ maxWidth: 240 }} value={incidentCategory} onChange={(e) => setIncidentCategory(e.target.value)}>
                <option value="">All categories</option>
                {Object.entries(CATEGORY_LABEL).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.label}
                  </option>
                ))}
              </Input>
              <label className="d-flex align-items-center mr-3 mb-2 small text-nowrap" style={{ cursor: "pointer" }}>
                <input
                  type="checkbox"
                  className="mr-1"
                  checked={showAcknowledgedIncidents}
                  onChange={(e) => setShowAcknowledgedIncidents(e.target.checked)}
                />
                Show acknowledged
              </label>
              <small className="text-muted ml-auto">{shownIncidents.length} shown</small>
            </div>
          )}
          {incidents && shownIncidents.length === 0 && <small className="text-muted">No incidents.</small>}
          {shownIncidents.length > 0 && (
            <Table size="sm" responsive hover>
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input
                      type="checkbox"
                      aria-label="Select all unacknowledged incidents"
                      checked={allIncidentsSelected}
                      disabled={selectableIncidents.length === 0}
                      onChange={() =>
                        setSelectedIncidents(allIncidentsSelected ? new Set() : new Set(selectableIncidents.map((i) => i.id)))
                      }
                    />
                  </th>
                  <th>When</th>
                  <th>Account</th>
                  <th>Severity</th>
                  <th>Category</th>
                  <th>Message</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shownIncidents.map((incident) => {
                  const open = openIncidents.has(incident.id);
                  const json = JSON.stringify(incident.details, null, 2);
                  return (
                    <React.Fragment key={incident.id}>
                      <tr
                        role="button"
                        onClick={() =>
                          setOpenIncidents((current) => {
                            const next = new Set(current);
                            if (next.has(incident.id)) next.delete(incident.id);
                            else next.add(incident.id);
                            return next;
                          })
                        }
                      >
                        <td onClick={(event) => event.stopPropagation()}>
                          {!incident.acknowledgedAt && (
                            <input
                              type="checkbox"
                              aria-label="Select incident"
                              checked={selectedIncidents.has(incident.id)}
                              onChange={() => toggleIncidentSelection(incident.id)}
                            />
                          )}
                        </td>
                        <td className="text-nowrap" title={incident.createdAt}>
                          {moment(incident.createdAt).format("MM-DD HH:mm:ss")}
                        </td>
                        <td>{incident.accountName ?? incident.credentialUid}</td>
                        <td>
                          <Badge color={incident.severity === "WARNING" ? "warning" : "danger"}>{incident.severity}</Badge>
                        </td>
                        <td>
                          <CategoryBadge category={incident.category} />
                        </td>
                        <td>{incident.message}</td>
                        <td className="text-right text-nowrap">
                          {!incident.acknowledgedAt && (
                            <Button
                              size="sm"
                              outline
                              color="warning"
                              onClick={(event) => {
                                event.stopPropagation();
                                void acknowledgeIncident([incident.id]);
                              }}
                            >
                              Acknowledge
                            </Button>
                          )}
                          {incident.acknowledgedAt && <small className="text-muted">ack {incident.acknowledgedBy}</small>}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={7}>
                            <Button size="sm" outline className="mb-2" onClick={() => copy(json)}>
                              Copy details
                            </Button>
                            <pre className="small mb-0" style={{ maxHeight: 320, overflow: "auto" }}>
                              {json}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Card className="mt-4" id="connectors-accounts">
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Connectors</h3>
          <Button
            size="sm"
            outline
            onClick={() => {
              void load();
              void loadBuilds();
              void loadBatches();
              void loadIncidents();
            }}
          >
            Refresh
          </Button>
        </CardHeader>
        <CardBody>
          {error && <Alert color="danger">{error}</Alert>}
          {status && !status.enabled && <Alert color="secondary">Native connector disabled on the controller.</Alert>}

          {items && (
            <Row>
              <Stat
                label="Native switch"
                value={status?.enabled ? "On" : "Off"}
                color={status?.enabled ? "success" : "secondary"}
                hint={`${status?.capableWorkers ?? 0} capable worker process(es)`}
              />
              <Stat label="Accounts" value={stats.accounts} hint={`${archivedCount} archived/invalid hidden`} />
              <Stat
                label="On native"
                value={stats.native}
                color="primary"
                hint={`${stats.accounts ? Math.round((stats.native / stats.accounts) * 100) : 0}% · ${stats.servers} server(s)`}
              />
              <Stat
                label="Enabled bots"
                value={stats.enabledBots}
                hint={`${stats.nativeBots} native · ${stats.metaapiBots} MetaApi`}
              />
              <Stat
                label="Connection"
                value={`${stats.connected} / ${stats.accounts}`}
                color={stats.degraded ? "warning" : "success"}
                hint={`${stats.connected} connected · ${stats.degraded} degraded · ${stats.notRunning} not running`}
              />
              <Stat
                label="Server builds"
                value={builds ? builds.length : buildsError ? "error" : "…"}
                color={builds?.some((row) => row.status === "NEW" && !row.acknowledgedAt) ? "warning" : undefined}
                hint={
                  builds
                    ? `${builds.filter((row) => row.status === "KNOWN").length} known · ${builds.filter((row) => row.status === "NEW").length} new · ${[
                        ...new Set(builds.map((row) => row.build)),
                      ].join(", ")}`
                    : buildsError ?? "loading"
                }
              />
              <Stat label="Eligible" value={stats.eligible} hint="password stored" />
              <Stat
                label="Connection tests"
                value={`${stats.testedPassed} / ${stats.testedPassed + stats.testedFailed}`}
                color={stats.testedFailed ? "warning" : undefined}
                hint="last test passed"
              />
              {(stats.fallbacks > 0 || stats.missingMetaapi > 0) && (
                <Stat
                  label="Attention"
                  value={stats.fallbacks + stats.missingMetaapi}
                  color="danger"
                  hint={`${stats.fallbacks} fallback(s) · ${stats.missingMetaapi} MetaApi missing`}
                />
              )}
            </Row>
          )}

          {fallbacks.length > 0 && (
            <Alert color="danger">
              <strong>{fallbacks.length} native fallback(s) active:</strong>
              <ul className="mb-0">
                {fallbacks.map((item) => (
                  <li key={item.uid}>
                    {item.exchangeAccountId && item.userId ? (
                      <Link to={`/users/${item.userId}/exchange-account/${item.exchangeAccountId}`}>{item.name}</Link>
                    ) : (
                      item.name
                    )}
                    {item.fallbackCause && (
                      <>
                        {" "}
                        — {moment(item.fallbackCause.at).format("HH:mm")}{" "}
                        <CategoryBadge category={item.fallbackCause.category} raw={item.fallbackCause.message} />{" "}
                        <span title={item.fallbackCause.reason}>{item.fallbackCause.message}</span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              Open the account to review and clear.
            </Alert>
          )}

          {items && (
            <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 12 }}>
              <Input
                bsSize="sm"
                style={{ width: 280 }}
                placeholder="Search account, user, server, login"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <select
                className="form-control"
                style={{ width: 340, height: 31, padding: "2px 8px", fontSize: 13 }}
                value=""
                title="Adds every eligible MetaApi account on the chosen server to the mass-migration selection"
                onChange={(event) => selectEligibleOnServer(event.target.value)}
              >
                <option value="">Select all eligible MetaApi accounts on server…</option>
                {serverNames.map((server) => (
                  <option key={server} value={server}>
                    {server} ({eligibleByServer.get(server) ?? 0})
                  </option>
                ))}
              </select>
              <label className="mb-0 d-flex align-items-center text-nowrap" style={{ gap: 6, fontSize: 13 }}>
                <input type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} />
                Show archived / invalid ({archivedCount})
              </label>
              {selected.size > 0 && (
                <span>
                  <strong>{selected.size} selected</strong>{" "}
                  <Button size="sm" color="primary" onClick={() => setMigrateTarget("MT5_NATIVE")}>
                    Migrate → MT5 native
                  </Button>{" "}
                  <Button size="sm" color="secondary" onClick={() => setMigrateTarget("METAAPI")}>
                    Migrate → MetaApi
                  </Button>{" "}
                  <Button size="sm" color="link" onClick={() => setSelected(new Set())}>
                    Clear
                  </Button>
                </span>
              )}
              <small className="text-muted ml-auto">{visible.length} shown</small>
            </div>
          )}

          {!items && !error && <Spinner />}
          {items && (
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} title="Select all in the current filter" />
                  </th>
                  {header("name", "Account")}
                  {header("user", "User")}
                  {header("server", "Server / login")}
                  {header("connector", "Connector")}
                  {header("connection", "Connection")}
                  {header("bots", "Bots")}
                  {header("lastTest", "Last connection test")}
                  {header("eligible", "Eligibility")}
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => {
                  const window = item.serverSwitchWindow;
                  const left = window ? new Date(window.until).getTime() - now : 0;
                  return (
                    <tr key={item.uid} className={item.archived ? "text-muted" : undefined}>
                      <td>
                        <input type="checkbox" checked={selected.has(item.uid)} onChange={() => toggle(item.uid)} />
                      </td>
                      <td>
                        {item.exchangeAccountId && item.userId ? (
                          <Link to={`/users/${item.userId}/exchange-account/${item.exchangeAccountId}`}>{item.name}</Link>
                        ) : (
                          item.name
                        )}
                        {item.archived && (
                          <Badge color="light" className="ml-1">
                            archived
                          </Badge>
                        )}
                      </td>
                      <td>{item.userEmail ?? "n/a"}</td>
                      <td>
                        {item.server} / {item.username}
                      </td>
                      <td>
                        <ConnectorBadge item={item} />
                        {item.serverLiveNative && !(window && left > 0) && item.connector === "METAAPI" && item.eligible && (
                          <div className="small text-success" title={`Live native accounts on this server, e.g. ${item.serverLiveNative.accountName}`}>
                            switch allowed (server live on native)
                          </div>
                        )}
                        {window && left > 0 && item.connector === "METAAPI" && item.eligible && (
                          <div className="small text-success" title={`Passing test on ${window.accountName}`}>
                            switch allowed (server test, {formatLeft(left)} left)
                          </div>
                        )}
                      </td>
                      <td>
                        <ConnectionBadge item={item} />
                      </td>
                      <td title={`${item.botsEnabled} enabled of ${item.botsTotal}`}>
                        {item.botsEnabled}
                        <small className="text-muted"> / {item.botsTotal}</small>
                      </td>
                      <td>
                        <LastTest item={item} />
                      </td>
                      <td>{item.eligible ? <Badge color="success">eligible</Badge> : <small className="text-danger">{item.ineligibleReason}</small>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Card className="mt-4" id="connectors-builds">
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Server builds</h3>
          <Button size="sm" outline onClick={() => void loadBuilds()}>
            Refresh
          </Button>
        </CardHeader>
        <CardBody>
          {buildsError && <Alert color="danger">{buildsError}</Alert>}
          {!builds && !buildsError && <Spinner />}
          {builds && builds.length === 0 && <small className="text-muted">No server builds recorded yet.</small>}
          {builds && builds.length > 0 && (
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th>Server</th>
                  <th>Build</th>
                  <th>Status</th>
                  <th>First seen</th>
                  <th>Last seen</th>
                  <th>Seen</th>
                  <th>Source</th>
                  <th>Native accounts</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {builds.map((row) => (
                  <tr key={row.id}>
                    <td>{row.server}</td>
                    <td>{row.build}</td>
                    <td>
                      <BuildStatusBadge row={row} />
                    </td>
                    <td title={row.firstSeenAt}>{moment(row.firstSeenAt).fromNow()}</td>
                    <td title={row.lastSeenAt}>{moment(row.lastSeenAt).fromNow()}</td>
                    <td>{row.seenCount}</td>
                    <td>{row.source}</td>
                    <td>{row.nativeCredentials}</td>
                    <td className="text-right">
                      {row.status === "NEW" && !row.acknowledgedAt && (
                        <Button size="sm" color="warning" disabled={acknowledging === row.id} onClick={() => void acknowledge(row)}>
                          Acknowledge
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={migrateTarget !== null} toggle={() => !migrating && setMigrateTarget(null)}>
        <ModalHeader toggle={() => !migrating && setMigrateTarget(null)}>
          Migrate {selected.size} account(s) → {migrateTarget === "METAAPI" ? "MetaApi" : "MT5 native"}
        </ModalHeader>
        <ModalBody>
          {migrateError && <Alert color="danger">{migrateError}</Alert>}
          <p className="mb-2">
            Accounts are switched in a staggered way: at most <strong>{concurrency}</strong> reconnect at a time, one start every{" "}
            <strong>{delayMinutes}</strong> min. A failed account is retried twice, then reverted to MetaApi; the batch pauses after 2 failures.
          </p>
          <Table size="sm">
            <thead>
              <tr>
                <th>Server</th>
                <th>Accounts</th>
                {migrateTarget === "MT5_NATIVE" && <th>Server test</th>}
              </tr>
            </thead>
            <tbody>
              {[...selectedServers.entries()].map(([server, accounts]) => {
                const window = accounts[0]?.serverSwitchWindow;
                const open = windowOpen(server);
                return (
                  <tr key={server}>
                    <td>{server}</td>
                    <td>{accounts.length}</td>
                    {migrateTarget === "MT5_NATIVE" && (
                      <td>
                        {accounts[0]?.serverLiveNative ? (
                          <span className="text-success">
                            no test needed: {accounts[0].serverLiveNative.liveAccounts} account(s) live on native (via{" "}
                            {accounts[0].serverLiveNative.accountName})
                          </span>
                        ) : open && window ? (
                          <span className="text-success">open, {formatLeft(new Date(window.until).getTime() - now)} left (via {window.accountName})</span>
                        ) : (
                          <Button size="sm" outline color="info" disabled={testingServer !== null} onClick={() => void runServerTest(server)}>
                            {testingServer === server ? <Spinner size="sm" /> : "Run test"}
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Row form>
            <Col xs="6">
              <label className="form-control-label">Max at the same time</label>
              <Input type="number" min={1} max={10} value={concurrency} onChange={(e) => setConcurrency(Math.min(10, Math.max(1, Number(e.target.value) || 1)))} />
            </Col>
            <Col xs="6">
              <label className="form-control-label">Delay between starts (min)</label>
              <Input type="number" min={0} max={60} step={0.5} value={delayMinutes} onChange={(e) => setDelayMinutes(Math.min(60, Math.max(0, Number(e.target.value) || 0)))} />
            </Col>
          </Row>
          {nativeNeedsTests && (
            <Button size="sm" color="info" outline className="mb-2 mr-2" disabled={testingAll || testingServer !== null} onClick={() => void runAllServerTests()}>
              {testingAll ? <Spinner size="sm" /> : "Test all servers"}
            </Button>
          )}
          {nativeNeedsTests && <small className="text-muted">A passing test within the last 15 minutes is required on every server above that has no account live on native yet; it authorizes the whole batch.</small>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline disabled={migrating} onClick={() => setMigrateTarget(null)}>
            Cancel
          </Button>
          <Button color="primary" disabled={migrating || nativeNeedsTests} onClick={() => void startMigration()}>
            {migrating ? <Spinner size="sm" /> : `Start migration of ${selected.size}`}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default Connectors;
